import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { formatPrice, readCurrencyPreference, saveCurrencyPreference } from '../lib/pricing';

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currency, setCurrencyState] = useState(null);
  const [country, setCountry] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paymentCurrencies, setPaymentCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const revision = useRef(0);
  const saveQueue = useRef(Promise.resolve());

  const refresh = useCallback(async () => {
    const current = ++revision.current;
    setLoading(true);
    setError(false);
    const preference = readCurrencyPreference();
    try {
      const { data } = await api.get('/pricing/context', {
        params: preference ? { currency_preference: preference } : {}, timeout: 8000,
      });
      if (current !== revision.current) return;
      setCurrencyState(data.currency);
      setCountry(data.country);
      setPlans(data.plans);
      setPaymentCurrencies(data.payment_currencies);
      if (data.preference_source === 'account') saveCurrencyPreference(data.currency);
    } catch {
      if (current !== revision.current) return;
      // Country is unknown. Only an explicit choice overrides the USD fallback.
      setCountry(null);
      setCurrencyState(preference || 'USD');
      try {
        const { data } = await api.get('/plans', { timeout: 5000 });
        if (current !== revision.current) return;
        setPlans(data.plans);
        setPaymentCurrencies(data.payment_currencies);
      } catch { if (current === revision.current) setError(true); }
    } finally { if (current === revision.current) setLoading(false); }
  }, []);

  useEffect(() => { if (!authLoading) refresh(); }, [user?.user_id, authLoading, refresh]);

  const setCurrency = useCallback((value) => {
    if (!['INR', 'USD'].includes(value)) return;
    setCurrencyState(value);
    saveCurrencyPreference(value);
    // Serialize writes so fast toggles cannot persist an older selection last.
    saveQueue.current = saveQueue.current.catch(() => {}).then(() =>
      api.put('/pricing/preference', { currency_preference: value }, { timeout: 8000 })
    ).catch(() => {
      if (user) toast.error('Currency saved on this device, but account sync failed. Select it again to retry.');
    });
  }, [user]);

  const price = (planId) => loading ? '' : formatPrice(plans.find((p) => p.id === planId)?.prices?.[currency]?.amount, currency);
  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, country, plans, price,
      currencies: ['INR', 'USD'], paymentCurrencies, loading, error, refresh }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);